<template>
  <div
    class="widget-container"
    :style="containerStyle"
    :class="{ 
      'widget-selected': isSelected, 
      'widget-resizing': isResizing,
      'widget-dragging': isDragging
    }"
    @mouseenter="showControls = true"
    @mouseleave="showControls = false"
  >
    <!-- Selection Indicator -->
    <div v-if="isSelected" class="selection-indicator">
      <v-icon color="primary">mdi-cursor-move</v-icon>
      <span class="selection-text">Click a green grid spot to move here</span>
    </div>

    <!-- Resize Preview Outline -->
    <div 
      v-if="isResizing && resizePreview" 
      class="resize-preview"
      :style="resizePreviewStyle"
    ></div>

    <!-- Move Handle - Top Left -->
    <div
      v-show="showControls"
      class="move-handle"
      :class="{ 'move-handle-dragging': isDragging }"
      @mousedown="startDrag"
      title="Drag to move widget"
    >
      <v-icon small color="white">mdi-cursor-move</v-icon>
    </div>

    <!-- Widget Controls - Top Right -->
    <div v-show="showControls" class="widget-controls">
      <v-btn
        icon
        small
        color="primary"
        @click.stop="editWidget"
        class="control-btn"
      >
        <v-icon small>mdi-pencil</v-icon>
      </v-btn>
      <v-btn
        icon
        small
        color="error"
        @click.stop="removeWidget"
        class="control-btn"
      >
        <v-icon small>mdi-delete</v-icon>
      </v-btn>
    </div>

    <!-- Widget Content -->
    <v-card
      class="widget-card"
      :class="{ 'widget-selected-card': isSelected }"
      elevation="2"
    >
      <v-card-title class="widget-header">
        <span class="widget-title">{{ widget.title }}</span>
      </v-card-title>
      
      <v-card-text class="widget-content">
        <WidgetRenderer
          :widget="widget"
          @update="handleWidgetUpdate"
        />
      </v-card-text>
    </v-card>

    <!-- Resize Handle - Bottom Right -->
    <div
      v-show="showControls && !isSelected"
      class="resize-handle"
      @mousedown="startResize"
      title="Drag to resize"
    >
      <v-icon small color="white">mdi-resize-bottom-right</v-icon>
    </div>
  </div>
</template>

<script>
import WidgetRenderer from './WidgetRenderer.vue'

export default {
  name: 'WidgetContainer',
  components: {
    WidgetRenderer
  },
  props: {
    widget: {
      type: Object,
      required: true
    },
    gridColumns: {
      type: Number,
      default: 12
    },
    isSelected: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showControls: false,
      isResizing: false,
      isDragging: false,
      resizePreview: null,
      dragPreview: null,
      resizeStart: { x: 0, y: 0, width: 0, height: 0 },
      dragStart: { x: 0, y: 0, startX: 0, startY: 0 }
    }
  },
  computed: {
    containerStyle() {
      const { x, y, width, height } = this.widget.position
      return {
        gridColumn: `${x + 1} / ${x + 1 + width}`,
        gridRow: `${y + 1} / ${y + 1 + height}`,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
      }
    },
    resizePreviewStyle() {
      if (!this.resizePreview) return {}
      
      const currentPos = this.widget.position
      const previewWidth = this.resizePreview.width
      const previewHeight = this.resizePreview.height
      
      return {
        gridColumn: `${currentPos.x + 1} / ${currentPos.x + 1 + previewWidth}`,
        gridRow: `${currentPos.y + 1} / ${currentPos.y + 1 + previewHeight}`,
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        border: '2px dashed #2196F3',
        borderRadius: '4px',
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        pointerEvents: 'none',
        zIndex: 1001
      }
    }
  },
  methods: {
    editWidget() {
      console.log('Edit widget:', this.widget.id)
      this.$emit('edit', this.widget)
    },
    removeWidget() {
      this.$emit('remove', this.widget.id)
    },
    handleWidgetUpdate(updates) {
      this.$emit('update', {
        ...this.widget,
        ...updates
      })
    },
    startDrag() {
      this.isDragging = true
      this.$emit('start-drag', this.widget)
    },
    onDragPreview(newPosition) {
      this.$emit('drag-preview', { 
        widgetId: this.widget.id, 
        newPosition 
      })
    },
    endDrag() {
      this.isDragging = false
    },
    startResize() {
      this.isResizing = true
      this.$emit('start-resize', this.widget)
    },
    onResizePreview(newSize) {
      this.$emit('resize-preview', { 
        widgetId: this.widget.id, 
        newSize 
      })
    },
    endResize() {
      this.isResizing = false
    }
  }
}
</script>

<style scoped>
.widget-container {
  position: relative;
  min-height: 100px;
  width: 100%;
  height: 100%;
}

.widget-card {
  height: 100%;
  width: 100%;
  transition: box-shadow 0.2s ease;
  cursor: default;
  display: flex;
  flex-direction: column;
}

.widget-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.widget-selected {
  opacity: 0.8;
  transform: rotate(1deg);
  z-index: 1000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3) !important;
}

.widget-dragging {
  opacity: 0.7;
  transform: scale(1.02) rotate(2deg);
  z-index: 1001;
  box-shadow: 0 12px 32px rgba(33, 150, 243, 0.4) !important;
  transition: none;
}

.widget-selected-card {
  border: 2px solid #2196F3;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3) !important;
}

/* Widget Controls - Top Right */
.widget-controls {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  display: flex;
  gap: 4px;
}

.control-btn {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(4px);
}

.widget-header {
  padding: 12px 16px 8px 16px;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
}

.widget-title {
  font-size: 14px;
  font-weight: 600;
  color: #424242;
}

.widget-content {
  padding: 16px;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Selection Indicator */
.selection-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #424242;
  font-size: 12px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px 12px;
  border-radius: 4px;
  z-index: 20;
}

.selection-text {
  display: block;
  margin-top: 4px;
}

/* Resize Preview Outline */
.resize-preview {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border: 2px dashed #2196F3;
  border-radius: 4px;
  pointer-events: none;
}

/* Resize Handle - Bottom Right */
.resize-handle {
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 10;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
}

.resize-handle:hover {
  background: rgba(0, 0, 0, 0.7);
}

/* Move Handle - Top Left */
.move-handle {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
  width: 28px;
  height: 28px;
  background: rgba(33, 150, 243, 0.8);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.move-handle:hover {
  background: rgba(33, 150, 243, 1);
  transform: scale(1.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.move-handle-dragging {
  background: rgba(33, 150, 243, 1);
  border-color: rgba(255, 255, 255, 0.8);
  box-shadow: 0 2px 8px rgba(33, 150, 243, 0.4);
}

@media (max-width: 960px) {
  .widget-container {
    grid-column: 1 / -1 !important;
    grid-row: auto !important;
  }
}
</style> 